import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withKeys as _withKeys } from "vue"

const _hoisted_1 = ["colspan"]
const _hoisted_2 = ["colspan"]
const _hoisted_3 = ["colspan"]
const _hoisted_4 = {
  key: 2,
  id: "compensator",
  class: "scrollbar-compensator"
}
const _hoisted_5 = ["tabindex", "onClick", "onKeydown"]
const _hoisted_6 = ["rowspan"]
const _hoisted_7 = ["onMouseenter"]
const _hoisted_8 = ["rowspan"]
const _hoisted_9 = ["colspan"]
const _hoisted_10 = ["colspan"]
const _hoisted_11 = ["colspan"]
const _hoisted_12 = {
  key: 2,
  id: "compensator",
  class: "scrollbar-compensator"
}
const _hoisted_13 = {
  key: 0,
  scope: "row"
}
const _hoisted_14 = ["colspan"]

import { computed, onMounted, ref, toRefs, watch } from 'vue'
import { Table } from './table-base'

type Props = {
  bodyHeight?: string
  leftWidth?: string
  rightWidth?: string
  leftTextAlign?: 'left' | 'center' | 'right'
  rightTextAlign?: 'left' | 'center' | 'right'
  layout?: 'fixed' | 'auto'
  caption: string
  sortBy?: (i: number) => string | number
  sortDirection?: 'asc' | 'desc'
  tabbableRows?: boolean
  table?: Table<HeaderProps, BodyProps>
}

type Emits = {
  (event: 'mouseEnterCell', cell: BodyProps, centerX: number, centerY: number): void
  (event: 'mouseLeaveCell'): void
  (event: 'onClickRow', row?: BodyProps[]): void
  (event: 'nearEnd', edge: 'top' | 'bottom'): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TableBase',
  props: {
    bodyHeight: { default: 'auto' },
    leftWidth: { default: 'auto' },
    rightWidth: { default: 'auto' },
    leftTextAlign: { default: 'left' },
    rightTextAlign: { default: 'right' },
    layout: { default: 'auto' },
    caption: {},
    sortBy: { type: Function, default: undefined },
    sortDirection: { default: 'asc' },
    tabbableRows: { type: Boolean, default: false },
    table: { default: undefined }
  },
  emits: ["mouseEnterCell", "mouseLeaveCell", "onClickRow", "nearEnd"],
  setup(__props: any, { emit: __emit }) {

_useCssVars(_ctx => ({
  "248fb0d2": (_ctx.leftWidth),
  "d46fbed2": (_ctx.rightWidth),
  "67bbae14": (_ctx.bodyHeight),
  "a6af0592": (_ctx.layout),
  "00bfb829": (_ctx.rightTextAlign),
  "79af76ea": (rightTextAlignWebkit.value),
  "77029a38": (_ctx.leftTextAlign),
  "8b1df1f4": (leftTextAlignWebkit.value)
}))

const props = __props

const emit = __emit

const { table } = toRefs(props)

const bodyRef = ref<HTMLElement | null>(null)

const hovered = ref<BodyProps | undefined>()

const nearEdge = ref<boolean>(false)

const order = computed(() => {
  const indices: number[] = []
  for (let i = 0; i < (table.value?.body?.length ?? 0); i++) {
    indices.push(i)
  }
  if (!props.sortBy) {
    return indices
  }
  const sortBy = props.sortBy
  const sorted = indices.toSorted((a, b) => {
    if (props.sortDirection === 'desc') {
      return sortBy(a) < sortBy(b) ? 1 : -1
    } else {
      return sortBy(a) > sortBy(b) ? 1 : -1
    }
  })
  return sorted
})

const topHeaders = computed(() => table.value?.headers?.top)
const bottomHeaders = computed(() => table.value?.headers?.bottom)
const leftHeaders = computed(() =>
  table.value?.headers?.left ? order.value.map((i) => table.value?.headers?.left?.[i]) : undefined
)
const rightHeaders = computed(() =>
  table.value?.headers?.right ? order.value.map((i) => table.value?.headers?.right?.[i]) : undefined
)
const topLeftHeader = computed(() => table.value?.headers?.topLeft)
const topRightHeader = computed(() => table.value?.headers?.topRight)
const bottomLeftHeader = computed(() => table.value?.headers?.bottomLeft)
const bottomRightHeader = computed(() => table.value?.headers?.bottomRight)
const leftTextAlignWebkit = computed(() => `-webkit-${props.leftTextAlign ?? 'center'}`)
const rightTextAlignWebkit = computed(() => {
  return `-webkit-${props.rightTextAlign ?? 'center'}`
})

const sortedBody = computed(() => {
  return order.value.map((i) => table.value?.body[i])
})

const footerColSpan = computed(() => {
  return (
    (table.value?.body?.[0]?.length ??
      topHeaders.value?.length ??
      bottomHeaders.value?.length ??
      -1) + 1
  )
})

const bodyHasScrollbars = computed(() => {
  if (!bodyRef.value) {
    return false
  }
  return bodyRef.value.scrollHeight > bodyRef.value.clientHeight
})

const lastScrollTop = ref<number>(0)

function isWithinPercentageOfRange(value: number, totalRange: number, percentage: number): boolean {
  const percentageValue = totalRange * (percentage / 100)
  return value <= percentageValue || value >= totalRange - percentageValue
}

function scroll() {
  if (!bodyRef.value) return
  const { scrollTop, offsetHeight, scrollHeight } = bodyRef.value
  if (isWithinPercentageOfRange(scrollTop, scrollHeight - offsetHeight, 30)) {
    emit('nearEnd', lastScrollTop.value > scrollTop ? 'top' : 'bottom')
    nearEdge.value = true
  } else {
    nearEdge.value = false
  }
  lastScrollTop.value = scrollTop
}

onMounted(() => {
  if (!bodyRef.value) {
    return
  }
  bodyRef.value.addEventListener('scroll', scroll)
  const unsub = watch(
    () => table.value?.body,
    () => {
      nearEdge.value = false
    }
  )

  return () => {
    if (!bodyRef.value) {
      return
    }
    bodyRef.value.removeEventListener('scroll', scroll)
    unsub()
  }
})

function onMouseEnter(event: Event, cell: BodyProps) {
  hovered.value = cell
  const target = event.target as HTMLElement
  const rect = target.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2

  emit('mouseEnterCell', cell, centerX, centerY)
}

function onMouseLeave() {
  hovered.value = undefined
  emit('mouseLeaveCell')
}

function onRowClick(event: Event, row?: BodyProps[], preventDefault?: boolean) {
  if (event && preventDefault) event.preventDefault() // Prevent space bar scrolling
  emit('onClickRow', row)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("table", null, [
    _createElementVNode("caption", null, _toDisplayString(_ctx.caption), 1),
    _createElementVNode("thead", null, [
      _createElementVNode("tr", null, [
        (topLeftHeader.value || (leftHeaders.value && leftHeaders.value.length > 0))
          ? (_openBlock(), _createElementBlock("th", {
              key: 0,
              colspan: topLeftHeader.value?.colspan,
              style: _normalizeStyle(topLeftHeader.value?.style),
              class: _normalizeClass(["left", topLeftHeader.value?.class]),
              scope: "col"
            }, [
              (topLeftHeader.value)
                ? _renderSlot(_ctx.$slots, "header-cell", {
                    key: 0,
                    cell: topLeftHeader.value,
                    position: "topLeft"
                  })
                : _createCommentVNode("", true)
            ], 14, _hoisted_1))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(topHeaders.value, (header, i) => {
          return (_openBlock(), _createElementBlock("th", {
            key: i,
            scope: "col",
            colspan: header.colspan,
            style: _normalizeStyle(header.style),
            class: _normalizeClass(header.class)
          }, [
            header
              ? _renderSlot(_ctx.$slots, "header-cell", {
                  key: 0,
                  cell: header,
                  position: "top"
                })
              : _createCommentVNode("", true)
          ], 14, _hoisted_2))
        }), 128)),
        (topRightHeader.value || (rightHeaders.value && rightHeaders.value.length > 0))
          ? (_openBlock(), _createElementBlock("th", {
              key: 1,
              scope: "col",
              colspan: topRightHeader.value?.colspan,
              style: _normalizeStyle(topRightHeader.value?.style),
              class: _normalizeClass(["right", topRightHeader.value?.class])
            }, [
              (topRightHeader.value)
                ? _renderSlot(_ctx.$slots, "header-cell", {
                    key: 0,
                    cell: topRightHeader.value,
                    position: "topRight"
                  })
                : _createCommentVNode("", true)
            ], 14, _hoisted_3))
          : _createCommentVNode("", true),
        (bodyHasScrollbars.value)
          ? (_openBlock(), _createElementBlock("th", _hoisted_4))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("tbody", {
      ref_key: "bodyRef",
      ref: bodyRef
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedBody.value, (row, rowIndex) => {
        return (_openBlock(), _createElementBlock("tr", {
          key: rowIndex,
          tabindex: _ctx.tabbableRows ? 0 : -1,
          onClick: (e) => onRowClick(e, row),
          onKeydown: [
            _withKeys((e) => onRowClick(e, row), ["enter"]),
            _withKeys((e) => onRowClick(e, row, true), ["space"])
          ]
        }, [
          (leftHeaders.value && leftHeaders.value.length > 0 && leftHeaders.value[rowIndex])
            ? (_openBlock(), _createElementBlock("th", {
                key: 0,
                scope: "row",
                rowspan: leftHeaders.value[rowIndex]?.rowspan,
                style: _normalizeStyle(leftHeaders.value[rowIndex]?.style),
                class: _normalizeClass(["left", leftHeaders.value[rowIndex]?.class])
              }, [
                _renderSlot(_ctx.$slots, "header-cell", {
                  cell: leftHeaders.value[rowIndex],
                  position: "left"
                })
              ], 14, _hoisted_6))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (cell, cellIndex) => {
            return (_openBlock(), _createElementBlock(_Fragment, {
              key: `${rowIndex}-${cellIndex}`
            }, [
              cell
                ? (_openBlock(), _createElementBlock("td", {
                    key: 0,
                    style: _normalizeStyle(cell.style),
                    class: _normalizeClass(cell.class),
                    onMouseenter: (e) => onMouseEnter(e, cell),
                    onMouseleave: onMouseLeave
                  }, [
                    _renderSlot(_ctx.$slots, "body-cell", {
                      cell: cell,
                      rowIndex: rowIndex,
                      cellIndex: cellIndex,
                      position: "body"
                    })
                  ], 46, _hoisted_7))
                : _createCommentVNode("", true)
            ], 64))
          }), 128)),
          (rightHeaders.value && rightHeaders.value.length > 0 && rightHeaders.value[rowIndex])
            ? (_openBlock(), _createElementBlock("th", {
                key: 1,
                scope: "row",
                rowspan: rightHeaders.value[rowIndex]?.rowspan,
                style: _normalizeStyle(rightHeaders.value[rowIndex]?.style),
                class: _normalizeClass(["right", rightHeaders.value[rowIndex]?.class])
              }, [
                _renderSlot(_ctx.$slots, "header-cell", {
                  cell: rightHeaders.value[rowIndex],
                  position: "right"
                })
              ], 14, _hoisted_8))
            : _createCommentVNode("", true)
        ], 40, _hoisted_5))
      }), 128))
    ], 512),
    _createElementVNode("tfoot", null, [
      _createElementVNode("tr", null, [
        (bottomLeftHeader.value || (leftHeaders.value && leftHeaders.value.length > 0))
          ? (_openBlock(), _createElementBlock("th", {
              key: 0,
              scope: "row",
              colspan: bottomLeftHeader.value?.colspan,
              style: _normalizeStyle(bottomLeftHeader.value?.style),
              class: _normalizeClass([bottomLeftHeader.value?.class, "left"])
            }, [
              (bottomLeftHeader.value)
                ? _renderSlot(_ctx.$slots, "header-cell", {
                    key: 0,
                    cell: bottomLeftHeader.value,
                    position: "bottomLeft"
                  })
                : _createCommentVNode("", true)
            ], 14, _hoisted_9))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bottomHeaders.value, (header, i) => {
          return (_openBlock(), _createElementBlock(_Fragment, { key: i }, [
            header
              ? (_openBlock(), _createElementBlock("th", {
                  key: 0,
                  scope: "col",
                  colspan: header.colspan,
                  style: _normalizeStyle(header.style),
                  class: _normalizeClass(header.class)
                }, [
                  _renderSlot(_ctx.$slots, "header-cell", {
                    cell: header,
                    position: "bottom"
                  })
                ], 14, _hoisted_10))
              : _createCommentVNode("", true)
          ], 64))
        }), 128)),
        (bottomRightHeader.value || (rightHeaders.value && rightHeaders.value.length > 0))
          ? (_openBlock(), _createElementBlock("th", {
              key: 1,
              scope: "col",
              colspan: bottomRightHeader.value?.colspan,
              style: _normalizeStyle(bottomRightHeader.value?.style),
              class: _normalizeClass(["right", bottomRightHeader.value?.class])
            }, [
              (bottomRightHeader.value)
                ? _renderSlot(_ctx.$slots, "header-cell", {
                    key: 0,
                    cell: bottomRightHeader.value,
                    position: "bottomRight"
                  })
                : _createCommentVNode("", true)
            ], 14, _hoisted_11))
          : _createCommentVNode("", true),
        (bodyHasScrollbars.value)
          ? (_openBlock(), _createElementBlock("th", _hoisted_12))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("tr", null, [
        (bottomLeftHeader.value || bottomRightHeader.value || (leftHeaders.value && leftHeaders.value.length > 0))
          ? (_openBlock(), _createElementBlock("th", _hoisted_13))
          : _createCommentVNode("", true),
        _createElementVNode("th", {
          colspan: footerColSpan.value,
          scope: "row"
        }, [
          _renderSlot(_ctx.$slots, "footer", { position: "footer" })
        ], 8, _hoisted_14)
      ])
    ])
  ]))
}
}

})